import { useInputValue } from '../../hooks';
import { BUTTON_TYPE } from '../generic/Button';
import LabeledTextInput from '../generic/Forms/LabeledTextInput';
import type { ConfirmWithInputDialogProps } from '../../contexts/DialogContext';

import ConfirmDialog from './ConfirmDialog';

/**
 * Extends the basic confirmDialog with an input field that the user must type in to confirm.
 *
 * @param {Omit<ConfirmWithInputDialogProps, 'type'>} props - The props
 * @param {string} props.title - Alert title.
 * @param {string | React.ReactElement} props.body - Alert body.
 * @param {string} props.inputText - The text that the user must type in to confirm.
 * @param {() => void} props.onConfirm - Called when the user clicks confirm.
 * @param {() => void} props.onClose - Closes the modal.
 * @param {boolean} [props.allowEnterConfirm] - Confirms by pressing the "Enter" key.
 * @param {string} [props.confirmText] - Confirm button text.
 * @param {string}  [props.confirmBtnType] - Confirm button text.
 * @param {string} [props.cancelText] - Cancel button text.
 */
const ConfirmWithInputDialog = ({
  title,
  body,
  inputText,
  onConfirm,
  onClose,
  allowEnterConfirm = false,
  confirmText = 'Confirm',
  confirmBtnType = BUTTON_TYPE.ERROR, // Red makes users pay more attention.
  cancelText = 'Cancel',
}: Omit<ConfirmWithInputDialogProps, 'type'>) => {
  const [inputValue, onChange] = useInputValue();

  const bodyWithInput = (
    <>
      {body}
      <div className="margin-top-lg">
        <LabeledTextInput
          label={`Please type "${inputText}" to ${confirmText.toLocaleLowerCase()}`}
          size="large"
          value={inputValue}
          onChange={onChange}
          placeholder={`Please type "${inputText}"`}
        />
      </div>
    </>
  );

  //  Confirm button is disabled until the user types the inputText
  const isConfirmDisabled = inputValue.trim() !== inputText;

  return (
    <ConfirmDialog
      title={title}
      body={bodyWithInput}
      onClose={onClose}
      onConfirm={onConfirm}
      allowEnterConfirm={allowEnterConfirm}
      confirmText={confirmText}
      confirmBtnType={confirmBtnType}
      cancelText={cancelText}
      isConfirmButtonDisabled={isConfirmDisabled}
    />
  );
};

export default ConfirmWithInputDialog;
